import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import React from 'react'

import {DisabledContext} from '../../../../../contexts/DisabledContext'
import useBooleanState from '../../../../../hooks/useBooleanState'
import {EditButton} from '../../../EditButton/EditButton'
import EditSettingsForm from '../../../EditSettingsForm/EditSettingsForm'
import PopupError from '../../../PopupError/PopupError'

import styles from './NameSection.css'

import Input, {Size} from 'src/components/pages/PipelinesPages/components/common/Input'

type Props = {
  name: string
  error?: string
  onNameChange: (name: string) => void
  'aria-label'?: string
  onConfirmClick: () => void
  onBlur?: () => void
}

function NameSection({
  name,
  error,
  onNameChange,
  onConfirmClick,
  onBlur,
  'aria-label': ariaLabel,
}: Props) {
  const isDisabled = React.useContext(DisabledContext)
  const [lastSavedName, setLastSavedName] = React.useState(name)
  const [isEditing, enableEditing, disableEditing] = useBooleanState()

  const handleEditClick = () => {
    enableEditing()
    setLastSavedName(name)
  }

  const handleConfirm = () => {
    onConfirmClick()
    disableEditing()
  }

  const handleCancel = () => {
    onNameChange(lastSavedName)
    disableEditing()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.currentTarget.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm()
      return
    }

    if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const handleBlur = () => {
    onBlur?.()
    disableEditing()
  }

  return (
    <div className={styles.nameSection}>
      <PopupError error={error}>
        {!isEditing ? (
          <div role="presentation" onClick={handleEditClick} className={styles.heading}>
            <H2 className={styles.name} title={name}>
              {name}
            </H2>
            <EditButton
              iconClassName={styles.editIcon}
              className={styles.edit}
              disabled={isDisabled}
            />
          </div>
        ) : (
          <EditSettingsForm
            onBlur={handleBlur}
            onConfirmClick={handleConfirm}
            onCancelClick={handleCancel}
            inline
          >
            <Input
              size={Size.FULL}
              defaultValue={name}
              aria-label={ariaLabel}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </EditSettingsForm>
        )}
      </PopupError>
    </div>
  )
}

export default NameSection
